import { Component, inject, Input, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { createForm } from '@sonofabit/ng-core';
import { BuildingMergeDto, BuildingService } from '@artemis-software/wr-api';
import { NbToastrService } from '@nebular/theme';
import { firstValueFrom } from 'rxjs';

@Component({
  standalone: false,
  selector: 'wr-building-coordinates-dialog',
  templateUrl: './building-coordinates-dialog.component.html',
})
export class BuildingCoordinatesDialogComponent implements OnInit {
  private readonly buildingService = inject(BuildingService);
  private readonly toastr = inject(NbToastrService);

  @Input() buildingId!: string;
  @Input() initialLatitude?: number;
  @Input() initialLongitude?: number;

  form?: ReturnType<typeof this.createForm>;

  ngOnInit(): void {
    this.form = this.createForm();
  }

  private createForm() {
    return createForm(({ field }) => ({
      latitude: field({
        value: this.initialLatitude,
        validators: [
          Validators.required,
          Validators.min(-90),
          Validators.max(90),
          Validators.pattern(/^(-?\d{1,3}(\.\d{1,6})?)$/),
        ],
      }),
      longitude: field({
        value: this.initialLongitude,
        validators: [
          Validators.required,
          Validators.min(-180),
          Validators.max(180),
          Validators.pattern(/^(-?\d{1,3}(\.\d{1,6})?)$/),
        ],
      }),
    }));
  }

  async onSubmit() {
    this.form?.markAsTouched();

    if (!this.form?.valid) {
      this.toastr.warning(
        'Bitte gültige Koordinaten eingeben.',
        'Ungültige Eingabe'
      );
      return false;
    }

    try {
      const current = await firstValueFrom(this.buildingService.findById(this.buildingId));
      const payload: BuildingMergeDto = {
        id: this.buildingId,
        baseData: {
          ...current.baseData,
          coordinates: {
            latitude: this.form.value.latitude,
            longitude: this.form.value.longitude,
          },
        },
      };

      const building = await firstValueFrom(this.buildingService.merge(payload));
      this.toastr.success(
        'Koordinaten erfolgreich gespeichert.',
        'Gebäude aktualisiert'
      );
      return building;
    } catch {
      this.toastr.danger(
        'Koordinaten konnten nicht gespeichert werden.',
        'Fehler'
      );
      return false;
    }
  }

  get disableSubmit(): boolean {
    return !this.form?.valid;
  }
}
